import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { SmartListTransactionItem } from '@components/smart-list/GenericSmartList';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiCustomerService } from '@services/api-customer.service';
import { ApiTransactionService } from '@services/api-transaction.service';
import { SessionManager } from '@services/session-manager';
import { SharedService } from '@services/shared.service';
import { CouponModel } from '@shared/models/CouponModel';
import { CustomerGiveawaysModel, CustomerPhoneFindModel } from '@shared/models/CustomerModel';
import { PointTransactionType, CouponType, Country } from '@shared/models/Enums';
import { ScratchModel } from '@shared/models/ScratchModel';
import { TransactionAccumulateModel, TransactionTransferModel } from '@shared/models/TransactionModel';
import { isScratchWinner } from '@shared/scratch-probability';

@Component({
    selector: 'app-play-scratch',
    templateUrl: './play-scratch.component.html',
    styleUrls: ['./play-scratch.component.scss']
})
export class PlayScratchComponent implements OnInit, OnDestroy {

    @ViewChild('playScratchModal', { static: true }) playScratchModal: TemplateRef<any>; 

    @Input() selectedScratch: ScratchModel;
    @Input() transactionPackageId: number = null;
    @Input() country: Country = null;
    @Input() uniqueLinkCode: string = null;
    @Input() lastStickerId: number = null;
    @Output() onCloseEvent: EventEmitter<void> = new EventEmitter<void>();


    customerGiveaways: CustomerGiveawaysModel;

    hasPlayed = false;
    showScratch = false;
    isWinner = false;

    coupon: CouponModel;
    coupons: CouponModel[] = [];

    sale: SmartListTransactionItem;

    timeRemaining: string;  // Para mostrar el tiempo restante
    hasMoreAttemps = false;

    isCommunityMember = true; //default true for cases in which the scratch is not from a community

    assignedSuccess = false;

    private counterInterval: any;  // Para almacenar el interval

    constructor(public modalService: NgbModal,
        public sharedService: SharedService,
        public apiTransactionService: ApiTransactionService,
        public apiCustomerService: ApiCustomerService,
        private router: Router,
    ) { }

    ngOnInit(): void {

        const session = SessionManager.getSession(true);
        if (session) {

            if(this.country == null) this.country = session.user.country;

            this.coupons = this.selectedScratch?.coupons?.filter(x => (x.communityId == null || x.approvedAt != null) && x.isActive &&
                (x.maxQuantity - x.quantityAccepted) > 0 && new Date(x.assignExpirationAt) > new Date()
            ); 

            this.sharedService.setLoading(true);

            this.hasPlayed = false;
            this.customerGiveaways = null;

            const customer = new CustomerPhoneFindModel({
                phoneCountry: session.user.phoneCountry,
                phone: session.user.phone.replace(/ /g, '').replace(/-/g, '').trim(),
                scratchId: this.selectedScratch.id,
                transactionPackageId: this.transactionPackageId
            });

            if (this.selectedScratch?.communityId > 0) customer.communityId = this.selectedScratch.communityId;

            
            //find the customer w the phone number
            this.apiCustomerService.findCustomerGiveawaysByPhone(customer, this.country).then(customer => {
                
                this.customerGiveaways = customer;
                
                //cant play
                if(this.selectedScratch.communityId > 0 && (customer.approvedAt == null || customer.rejectedAt != null)) 
                    this.isCommunityMember = false;

                // Check if the scratch has available rewards
                if ((this.selectedScratch?.rewardsQuantity - this.selectedScratch?.redeemedQuantity) > 0 || this.selectedScratch?.rewardsQuantity > 0) {

                    //check if the user already played
                    this._checkCanPlay();

                } else {

                    //no rewards, let the user play and lose
                    this._checkCanPlayWithOutRewards();
                }
            }).catch(
                (err) => {

                    //not found number go to register
                    this.coupon = null;
                    this.sharedService.setLoading(false);
                }
            );
        }
    }

    ngOnDestroy() {

        // Limpiar el intervalo cuando el componente se destruye
        if (this.counterInterval) {
            clearInterval(this.counterInterval);
        }
    }

    onCloseSucess(){
        this.assignedSuccess = false;
    }

    onOpen(modal) {
        
        const modalRef = this.modalService.open(modal, {
            keyboard: false, backdrop: 'static',
            centered: true, windowClass: 'modal-full'
        });

        modalRef.result.finally(() => {

            // add a loader at the close time to avoid the reopening of the modal
            this.sharedService.setLoading(true);

            setTimeout(() => {

                this.onCloseEvent.emit();

                this.sharedService.setLoading(false);
            }, 500);
        });
    }

    onGoCommunity(){

        this.router.navigate(['/community-join', this.selectedScratch.communityId]);
        this.modalService.dismissAll();

    }

    onClose() {

        this.modalService.dismissAll();
    }

    checkWinner() {

        this.showScratch = true;
        this.hasPlayed = false;

        if (isScratchWinner(this.selectedScratch?.probability)) {

            this.setReward();

        } else {

            this.isWinner = false;
        }

        this.sharedService.setLoading(false);
    }

    setReward() {

        // Primero calculamos el total de disponibilidad sumando el stock de todos los cupones
        const totalAvailableStock = this.coupons.reduce((total, coupon) => {
            return total + (coupon.maxQuantity - coupon.quantityAccepted); // stock disponible por cada cupón
        }, 0);

        // Si no hay cupones disponibles, no es un ganador
        if (totalAvailableStock === 0) {
            this.isWinner = false;
            return;
        }

        // Generamos un número aleatorio entre 1 y el total de stock disponible
        const randomStockNumber = Math.floor(Math.random() * totalAvailableStock) + 1;
        let cumulativeStock = 0;

        // Recorremos los cupones para determinar cuál corresponde al número aleatorio generado
        for (const coupon of this.coupons) {
            const availableStock = coupon.maxQuantity - coupon.quantityAccepted; // Calculamos stock disponible por cupón

            // Vamos sumando el stock acumulado
            cumulativeStock += availableStock;

            // Si el número aleatorio cae dentro del rango de este cupón, lo seleccionamos
            if (randomStockNumber <= cumulativeStock) {
                this.coupon = coupon;
                this.isWinner = true;
                break;
            }
        }

        // Si no encontramos un cupón, no es un ganador
        if (!this.coupon) {
            this.isWinner = false;
        }
    }

    onClaimGiveaway() {

        //claim the coupon
        this.sharedService.setLoading(true);

        let givi = new TransactionTransferModel({
            id: this.coupon.id,
            type: PointTransactionType.ReceivedCoupons,
            customerEmail: this.customerGiveaways.email,
            customerId: this.customerGiveaways.id,
            scratchId: this.selectedScratch?.id
        });

        if (this.uniqueLinkCode != null) givi.uniqueLinkCode = this.uniqueLinkCode;

        if(this.lastStickerId != null) givi.loyaltyPlanCustomerStickerId = this.lastStickerId;

        this._uniqueLink(givi);
    }

    //accumulate coupon

    private _uniqueLink(givi: TransactionTransferModel): void {

        this.apiTransactionService.getUniqueLink(givi, this.country)
            .then((transaction) => {

                if (this.coupon.points > 0) {

                    this._getTransactionAfterReclaim(transaction.id);
                } else {

                    this.sharedService.setLoading(false);

                    //show assigned givi
                    this.assignedSuccess = true;
                }
            })
            .catch((err) => {

                this.sharedService.setLoading(false);
                this.showInvalidLinkAlert(err.error?.Message);
            });
    }

    private _getTransactionAfterReclaim(id: number): void {

        this.apiTransactionService.getFullTransaction(id, this.country).then(
            (transaction) => {

                this.sale = new SmartListTransactionItem({
                    userId: id,
                    ...transaction,
                });

                this.accumulateCoupon();
            },
            (err) => {
                this.sharedService.setLoading(false);
                let errMsg = `Error al cargar información del Givi Bit`;
                if (err.status == 404) {
                    errMsg = `El Givi Bit no existe`;
                }
                this.sharedService.showErrorAlert(null, errMsg);
            }
        );
    }

    private accumulateCoupon() {

        let accumulateCoupon = new TransactionAccumulateModel({
            transactionId: this.sale.id,
            vendorUserId: this.sale.vendorId,
            shippingProviderId: this.sale.shippingProviderId,
            isSuccessful: null,
            messageResult: null,
        });

        this.apiTransactionService.accumulateCoupon(accumulateCoupon, this.country).then(
            (x) => {

                this.sharedService.setLoading(false);

                //show assigned givi
                this.assignedSuccess = true;
            },
            (err) => {

                this.sharedService.setLoading(false);
                let errMsg = `Podés acumular el bit desde mis givis`;
                this.sharedService.showErrorAlert(null, errMsg);
            }
        );
    }

    private showInvalidLinkAlert(message: string) {

        const errMsg = message;
        const session = this.apiTransactionService.getSession();
        const logged = session && session.user.email;
        this.sharedService
            .showErrorAlertCoupon("Givi inválido", errMsg, "Entendido")
    }

    private _cantPlay() { 

        this.hasPlayed = true;
        this.showScratch = false
        this.sharedService.setLoading(false);
    }

    private _checkCanPlay() {
        
        if (this.customerGiveaways.scratchId != null) {

            if (this.selectedScratch.maxAttempts == 1) {

                //if the customerGiveaways has any scratchId, means that the user already play it
                this._cantPlay();

                this.timeRemaining = null;
                this.hasMoreAttemps = false;

            } else if (this.selectedScratch.maxAttempts <= this.customerGiveaways.giveaways.length) {

                //ya realizo el maximo de intentos permitidos
                this._cantPlay();

                this.timeRemaining = null;
                this.hasMoreAttemps = false;

            } else {

                let lastIndex = this.customerGiveaways.giveaways.length - 1;
                
                const lastPlayed = new Date(this.customerGiveaways?.giveaways[lastIndex]?.createdAt)
                const dateToPlay = new Date(lastPlayed.getTime() + this.selectedScratch.intervalHours * 60 * 60 * 1000);

                //El tiempo no ha pasado
                if (dateToPlay > new Date()) {

                    //No ha cumplido el tiempo para volver a jugar min 1 h
                    this._cantPlay();
                    this.hasMoreAttemps = true;
                    this._startCounter(dateToPlay);  // Inicia el contador
                } else {

                    //volver a jugar 
                    this.checkWinner();
                }

            }

            //let the user play the scratch
        } else this.checkWinner();

        this.onOpen(this.playScratchModal);
    }

    private _checkCanPlayWithOutRewards() {

        if (this.customerGiveaways.scratchId != null) {

            if (this.selectedScratch.maxAttempts == 1) {

                //if the customerGiveaways has any scratchId, means that the user already play it
                this._cantPlay();

                this.timeRemaining = null;
                this.hasMoreAttemps = false;

            } else if (this.selectedScratch.maxAttempts <= this.customerGiveaways.giveaways.length) {

                //ya realizo el maximo de intentos permitidos
                this._cantPlay();

                this.timeRemaining = null;
                this.hasMoreAttemps = false;

            } else {

                let lastIndex = this.customerGiveaways.giveaways.length - 1;

                const lastPlayed = new Date(this.customerGiveaways?.giveaways[lastIndex]?.createdAt)
                const dateToPlay = new Date(lastPlayed.getTime() + this.selectedScratch.intervalHours * 60 * 60 * 1000);

                //El tiempo no ha pasado
                if (dateToPlay > new Date()) {

                    //No ha cumplido el tiempo para volver a jugar min 1 h
                    this._cantPlay();
                    this.hasMoreAttemps = true;
                    this._startCounter(dateToPlay);  // Inicia el contador
                }
            }
        }
        
        this.showScratch = true;
        this.isWinner = false;
        this.sharedService.setLoading(false);

        this.onOpen(this.playScratchModal);
    }

    private _startCounter(dateToPlay: Date) {
        this.counterInterval = setInterval(() => {
            const now = new Date().getTime();
            const timeLeft = dateToPlay.getTime() - now;

            if (timeLeft > 0) {
                const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
                const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
            
                if (days > 0) {
                    this.timeRemaining = `${days}d ${hours}h ${minutes}m ${seconds}s`;
                } else {
                    this.timeRemaining = `${hours}h ${minutes}m ${seconds}s`;
                }
            } else {
                clearInterval(this.counterInterval);
                this.timeRemaining = '¡Ya puedes volver a jugar!';
            }
            
        }, 1000);
    }
}
